"use client";

import { useCallback, useRef } from "react";
import { postJson } from "@lib/http";
import { describeActionOutcome, parseActionToken } from "@lib/agent/actionToken";
import { createSseFrameReader, type SseFrame } from "@lib/agent/sseFrames";
import type { AgentPageContext } from "@lib/agent/pageContext";
import { randomId } from "@lib/randomId";
import type { AgentEventType, LessonContext, OverlayMessage, OverlayState } from "./types";

/** One prior chat turn replayed to the tutor stream so it sees the conversation. */
export type AgentHistoryMessage = { role: "user" | "assistant"; content: string };

/** How many recent turns to replay (server caps this again). */
export const AGENT_HISTORY_LIMIT = 8;

/**
 * Map the visible transcript to role-tagged turns for the model: learner
 * bubbles become `user`, tutor bubbles become `assistant`; still-streaming and
 * empty bubbles and conversation-mode personas are dropped. A tutor turn that
 * proposed an action survives even with no prose, carrying what the learner
 * did with it, so the tutor doesn't re-propose what it already did (CON-123).
 * Returns the last `limit` turns.
 */
export function overlayMessagesToHistory(
  messages: OverlayMessage[],
  limit: number = AGENT_HISTORY_LIMIT,
): AgentHistoryMessage[] {
  const mapped: AgentHistoryMessage[] = [];
  for (const m of messages) {
    if (m.isStreaming) continue;
    const text = m.text.trim();
    if (m.role === "user") {
      if (text) mapped.push({ role: "user", content: text });
    } else if (m.role === "tutor") {
      const outcome = m.action ? describeActionOutcome(m.action, m.actionStatus) : "";
      const content = [text, outcome].filter(Boolean).join("\n");
      if (content) mapped.push({ role: "assistant", content });
    }
  }
  return mapped.slice(-limit);
}

export type AgentPromptPayload = {
  pageContext: AgentPageContext | null;
  lessonContext: LessonContext | null;
  userMessage?: string;
  history?: AgentHistoryMessage[];
};

export type PromptTrigger = AgentEventType | "user_message";

export type PromptHistoryEntry = {
  id: string;
  ts: number;
  trigger: PromptTrigger;
  payload: AgentPromptPayload;
  streamError?: string;
};

const TUTOR_UNAVAILABLE = "Sorry, the tutor is unavailable right now. Please try again later.";
const PROMPT_HISTORY_LIMIT = 50;

function snapshotPromptPayload(payload: AgentPromptPayload): AgentPromptPayload {
  return JSON.parse(JSON.stringify(payload)) as AgentPromptPayload;
}

async function readHttpErrorDetail(res: Response): Promise<string> {
  try {
    const json = (await res.json()) as { error?: unknown };
    if (typeof json.error === "string" && json.error.trim()) {
      return json.error.trim();
    }
  } catch {
    // response body is not JSON
  }
  return `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`;
}

function recordStreamError(
  promptHistoryRef: React.MutableRefObject<PromptHistoryEntry[]>,
  lastStreamErrorRef: React.MutableRefObject<string | null>,
  historyEntryId: string,
  detail: string,
): void {
  lastStreamErrorRef.current = detail;
  const entry = promptHistoryRef.current.find((item) => item.id === historyEntryId);
  if (entry) {
    entry.streamError = detail;
  }
}

async function streamTutorResponse(
  payload: AgentPromptPayload,
  historyEntryId: string,
  signal: AbortSignal,
  setState: React.Dispatch<React.SetStateAction<OverlayState>>,
  promptHistoryRef: React.MutableRefObject<PromptHistoryEntry[]>,
  lastStreamErrorRef: React.MutableRefObject<string | null>,
): Promise<void> {
  const res = await postJson(
    "/api/agent/stream",
    {
      pageContext: payload.pageContext,
      lessonContext: payload.lessonContext,
      userMessage: payload.userMessage,
      history: payload.history,
    },
    { signal },
  );
  const messageId = randomId();
  setState((prev) => ({
    ...prev,
    messages: [...prev.messages, { id: messageId, text: "", isStreaming: true, role: "tutor" }],
  }));

  if (!res.ok || !res.body) {
    const detail = !res.ok ? await readHttpErrorDetail(res) : "Empty response body";
    recordStreamError(promptHistoryRef, lastStreamErrorRef, historyEntryId, detail);
    setState((prev) => ({
      ...prev,
      messages: prev.messages.map((m) =>
        m.id === messageId ? { ...m, text: TUTOR_UNAVAILABLE, isStreaming: false } : m,
      ),
    }));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  // Framing lives in @lib/agent/sseFrames so this loop, the scenario runner and
  // the native client cannot disagree about it. In particular the reader holds
  // a frame that a delivery cut in half, which this loop used to drop silently.
  const frames = createSseFrameReader();
  let streamFailed = false;
  let accumulated = "";

  /** Returns true when an in-band error frame ended the stream. */
  const consume = (batch: SseFrame[]): boolean => {
    for (const frame of batch) {
      if (frame.error) {
        streamFailed = true;
        recordStreamError(promptHistoryRef, lastStreamErrorRef, historyEntryId, frame.error);
        setState((prev) => ({
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === messageId ? { ...m, text: TUTOR_UNAVAILABLE, isStreaming: false } : m,
          ),
        }));
        return true;
      }
      const delta = frame.delta;
      if (delta) {
        accumulated += delta;
        setState((prev) => ({
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === messageId ? { ...m, text: m.text + delta } : m,
          ),
        }));
      }
    }
    return false;
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      // The final decode() flushes a trailing multi-byte sequence; flush() then
      // yields a last frame the server sent without a closing newline.
      consume(frames.push(decoder.decode()));
      consume(frames.flush());
      break;
    }
    if (consume(frames.push(decoder.decode(value, { stream: true })))) {
      await reader.cancel();
      break;
    }
  }

  if (!streamFailed) {
    lastStreamErrorRef.current = null;
    // D-13: a trailing [ACTION{...}] token becomes a confirm chip; malformed
    // tokens drop silently and the tip text still renders (research A4).
    const { display, action } = parseActionToken(accumulated);
    setState((prev) => ({
      ...prev,
      messages: prev.messages.map((m) =>
        m.id === messageId
          ? { ...m, text: display, isStreaming: false, action: action ?? undefined }
          : m,
      ),
    }));
  }
}

export type UseAgentStreamOptions = {
  setState: React.Dispatch<React.SetStateAction<OverlayState>>;
  buildPayload: () => Pick<AgentPromptPayload, "pageContext" | "lessonContext">;
  lastFiredRef: React.MutableRefObject<number | null>;
  pendingDelayUntilRef: React.MutableRefObject<number | null>;
  lastStreamErrorRef: React.MutableRefObject<string | null>;
  promptHistoryRef: React.MutableRefObject<PromptHistoryEntry[]>;
  /** Live mirror of the transcript, read at fire time to replay recent turns. */
  messagesRef?: React.MutableRefObject<OverlayMessage[]>;
};

export type FireOptions = {
  /** When false, stream a tutor tip without expanding the widget. Defaults to true. */
  open?: boolean;
  /** When true, play the collapsed-widget spring spin (stall nudge). */
  nudge?: boolean;
};

export type UseAgentStreamReturn = {
  fire: (userMessage?: string, trigger?: PromptTrigger, options?: FireOptions) => void;
  cancelStream: () => void;
  resetStreamHistory: () => void;
  lastPromptPayloadRef: React.MutableRefObject<AgentPromptPayload | null>;
};

export function useAgentStream({
  setState,
  buildPayload,
  lastFiredRef,
  pendingDelayUntilRef,
  lastStreamErrorRef,
  promptHistoryRef,
  messagesRef,
}: UseAgentStreamOptions): UseAgentStreamReturn {
  const abortRef = useRef<AbortController | null>(null);
  const lastPromptPayloadRef = useRef<AgentPromptPayload | null>(null);

  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const resetStreamHistory = useCallback(() => {
    lastPromptPayloadRef.current = null;
    promptHistoryRef.current = [];
    lastStreamErrorRef.current = null;
  }, [lastStreamErrorRef, promptHistoryRef]);

  const fire = useCallback(
    (
      userMessage?: string,
      trigger: PromptTrigger = "manual_help_requested",
      options?: FireOptions,
    ) => {
      const shouldOpen = options?.open ?? true;
      const shouldNudge = options?.nudge ?? false;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      lastFiredRef.current = Date.now();
      pendingDelayUntilRef.current = null;
      lastStreamErrorRef.current = null;
      // messagesRef still holds the transcript before this turn's user bubble
      // (appended via setState just before fire; the mirror updates post-commit).
      const history = messagesRef ? overlayMessagesToHistory(messagesRef.current) : undefined;
      const payload: AgentPromptPayload = { ...buildPayload(), userMessage, history };
      lastPromptPayloadRef.current = payload;
      const historyEntryId = randomId();
      promptHistoryRef.current = [
        ...promptHistoryRef.current,
        {
          id: historyEntryId,
          ts: Date.now(),
          trigger,
          payload: snapshotPromptPayload(payload),
        },
      ].slice(-PROMPT_HISTORY_LIMIT);
      setState((prev) => ({
        ...prev,
        ...(shouldOpen ? { isOpen: true } : {}),
        ...(shouldNudge ? { nudgeAt: Date.now() } : {}),
        messages: prev.messages.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m)),
      }));
      streamTutorResponse(
        payload,
        historyEntryId,
        controller.signal,
        setState,
        promptHistoryRef,
        lastStreamErrorRef,
      ).catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        // Undo only the open this call made. An unprompted turn opens nothing,
        // so a stream that dies mid-tip must not close a panel the learner
        // opened for themselves.
        if (!shouldOpen) return;
        setState((prev) => (prev.isOpen ? { ...prev, isOpen: false } : prev));
      });
    },
    [
      buildPayload,
      setState,
      lastFiredRef,
      pendingDelayUntilRef,
      lastStreamErrorRef,
      promptHistoryRef,
      messagesRef,
    ],
  );

  return { fire, cancelStream, resetStreamHistory, lastPromptPayloadRef };
}
